"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ComponentProps, memo, Suspense } from "react";
import { useStockListingFilters } from "../context/StockListingFiltersContext";
import { trackStockSortFilterBarCt, type StockSortCtAnalytics } from "./stockSortCt";

export interface StockSortChipProps {
  label: string;
  removeAriaLabel?: string;
  /** When provided, fires the new-way sort/filter-bar CT event on chip remove. */
  sortCtAnalytics?: StockSortCtAnalytics;
  /** CTA text for chip remove — defaults to the chip label. */
  clearCtaText?: string;
  resetSortValue?: string;
}

/**
 * Chip shown in the mobile header bar when a non-default sort is active.
 * Removing the chip clears the `sort` query param, resetting to the default sort.
 */
const StockSortChip = memo(
  ({
    label,
    removeAriaLabel,
    sortCtAnalytics,
    clearCtaText,
    resetSortValue,
  }: StockSortChipProps) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { setActiveSortValue } = useStockListingFilters();

    const handleRemove = () => {
      if (sortCtAnalytics) {
        void trackStockSortFilterBarCt({
          sectionTitle: sortCtAnalytics.sectionTitle,
          ctaText: clearCtaText ?? label,
          subSection: "clear filters",
        });
      }
      const params = new URLSearchParams(searchParams.toString());
      params.delete("sort");
      const queryString = params.toString();
      if (resetSortValue) {
        setActiveSortValue?.(resetSortValue);
      }
      router.replace(queryString ? `?${queryString}` : pathname, { scroll: false });
    };

    return (
      <div className="p-4 md:px-0">
        <span className="flex h-[30px] w-fit items-center gap-[6px] rounded-[4px] border border-[#D3DAE1] bg-[#F2F2F2] px-[6px] py-[8px] text-[12px] text-[#4A6784] font-medium">
          {label}
          <button
            type="button"
            onClick={handleRemove}
            className="ml-0.5 text-[11px] text-[#4A6784] hover:text-[#1A1A1A]"
            aria-label={removeAriaLabel ?? `Remove ${label} sort`}
          >
            ✕
          </button>
        </span>
      </div>
    );
  },
);

StockSortChip.displayName = "StockSortChip";

// useSearchParams() requires a Suspense boundary on statically prerendered routes.
const SuspendedStockSortChip = (
  props: ComponentProps<typeof StockSortChip>,
) => (
  <Suspense
    fallback={
      <div className="p-4 md:px-0" aria-hidden="true">
        <span className="block h-7.5 w-30 animate-pulse rounded-sm border border-gray-300 bg-lgray-200" />
      </div>
    }
  >
    <StockSortChip {...props} />
  </Suspense>
);

export default SuspendedStockSortChip;
